import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowRight, CalendarDays, Camera, Check, ChevronDown, ChevronRight, EyeOff, FileCheck, FilePenLine, HardHat, ListFilter, MessageSquare, Phone, Plus, Search, Shield, Star, Timer, Users, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { usePersistentState } from '../lib/usePersistentState';
import { formatUsd, localeUI } from '../lib/format';
import { useI18n } from '../lib/i18n';
import { Field } from '../components/ui';
import { RChip } from '../components/ui/RChip';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { statusColor } from '../lib/status';
import { LIFECYCLE, SIN_ETAPA, agruparPorEtapa, colorDeGrupo } from '../lib/lifecycle';
import { useScope, ScopeTabs } from '../lib/scope';
import { useAuth, hasRole } from '../lib/auth';
import { ClaimsBoardSync } from '../components/ClaimsBoardSync';
import { ClaimDrawer } from '../components/ClaimDrawer';
import { ClaimTypeChip } from '../components/ClaimTypeChip';
import { ClaimDepartmentTab } from '../components/ClaimDepartmentTab';


type Claim = {
  id: string;
  claim_number: string | null;
  homeowner_name: string | null;
  insurance_company: string | null;
  contractor_name: string | null;
  address?: string | null;
  rcv_amount_cents: string | number;
  fee_mode: string;
  current_status: string | null;
  lifecycle_status: string | null;
  status_since?: string | null;
  assigned_person: string | null;
  assigned_teams: Array<{ dept: string; team: string }> | null;
  team_member_names: string | null;
  last_follow_up_at?: string | null;
  next_follow_up_at?: string | null;
  /** Los TRES follow-ups con nombre: claims / supplements / payments. */
  /** Por departamento: last = follow-up REAL (null si nunca hubo), next/days_late solo si el
   *  depto está trabajando el claim, never = nunca hubo follow-up, idle = sin etapa que perseguir. */
  follow_ups_by_dept?: Record<string, { last: string | null; next: string | null; days_late: number | null; scheduled: boolean; never?: boolean; idle?: boolean }> | null;
  has_photo?: boolean;
  has_photos?: boolean;
  /** Cuántos updates tiene el claim (la burbuja azul, como Monday). */
  updates_count?: number;
  has_policy?: boolean;
  photos_url?: string | null;
  intake_status?: 'complete' | 'incomplete' | 'missing' | null;
  welcome_call_done?: boolean;
  /** Metido en la cola pegajosa "Welcome Call" de Scheduling AHORA MISMO — distinto de
   *  welcome_call_done: un claim puede llevar semanas en Claim Accepted (después de Claim
   *  Review, donde ReadinessChips ya no se muestra) y seguir atascado ahí sin que se vea. */
  in_welcome_call?: boolean;
  contract_signed?: boolean;
  owner_dept?: string | null;
  /** Los dos campos de los que sale el CHIP del tipo de claim (ver `lib/claimType`). */
  claim_type?: string | null;
  type_of_loss?: string | null;
  /** EL SUB-STATUS DE SUPPLEMENTING (dueño 2026-10-13): cuántas rondas del seguro ya tienen nuestro envío.
   *  Solo viaja en claims en Supplementing (backend supplements/intentos.ts). */
  supplement_attempt?: SupplementAttempt | null;
  /** Cuándo se mandó la LOR (null = no se ha mandado). Parte el grupo "Claim in" (dueño 2026-10-13). */
  lor_sent_at?: string | null;
  /** Status Pre-approved del depto Supplements: grupo propio "Supplement pre-approved" (dueño 2026-10-13). */
  supplement_preapproved?: boolean;
  /** Estado de mitigación (grupo "Mitigation" después de Approved; el claim sigue también en su etapa). */
  mitigation?: { kind: 'to_estimate' | 'estimate'; attempt: number; estimates: number; follow_ups: number; estimate_at: string | null; last_sent_at: string | null } | null;
};
/** Grupos VIRTUALES de la página (no son etapas del ciclo de vida): dónde van y de qué se alimentan. */
const GRUPO_PREAPROBADO = 'Supplement pre-approved';
const GRUPO_MITIGACION = 'Mitigation';
/**
 * Inserta los grupos virtuales: "Supplement pre-approved" (claims con ese status en Supplements, se SACAN de
 * Supplementing) después de Supplementing, y "Mitigation" (claims con estimado de mitigación o por estimar: COPIAS,
 * el claim sigue en su etapa, como en el board de Supplements) después de Approved.
 */
function conGruposVirtuales(groups: Array<{ stage: string; rows: Claim[] }>, visible: Claim[]): Array<{ stage: string; rows: Claim[] }> {
  const out = groups.map((g) => (g.stage === 'Supplementing' ? { ...g, rows: g.rows.filter((c) => !c.supplement_preapproved) } : g)).filter((g) => g.rows.length);
  const insertarDespuesDe = (etapa: string, grupo: { stage: string; rows: Claim[] }) => {
    if (!grupo.rows.length) return;
    const idxEtapa = LIFECYCLE.indexOf(etapa);
    // Después del grupo de esa etapa; si no está, antes del primer grupo de una etapa posterior.
    let pos = out.findIndex((g) => g.stage === etapa);
    if (pos >= 0) pos += 1;
    else { pos = out.findIndex((g) => { const i = LIFECYCLE.indexOf(g.stage); return i === -1 || i > idxEtapa; }); if (pos < 0) pos = out.length; }
    out.splice(pos, 0, grupo);
  };
  insertarDespuesDe('Supplementing', { stage: GRUPO_PREAPROBADO, rows: visible.filter((c) => c.supplement_preapproved) });
  insertarDespuesDe('Approved', { stage: GRUPO_MITIGACION, rows: visible.filter((c) => c.mitigation) });
  return out;
}
type SupplementAttempt = {
  attempt: number;
  sub_status: 'not_submitted' | 'submitted' | 'second_attempt' | 'third_attempt' | 'nth_attempt';
  rounds: number; counters: number;
  last_sent_at: string | null; last_round_at: string | null;
  carrier_replied: boolean; counter_ready: boolean;
};
/**
 * Sub-grupos de "Supplementing" (dueño 2026-10-13: "Supplement Submitted, después Second Attempt y luego Third
 * Attempt… y así sucesivamente"). Lo que todavía no se mandó va PRIMERO: es lo que hay que trabajar. Después, un
 * sub-grupo por intento en orden. La llave sirve para colapsar cada uno por separado.
 */
type SubGrupo = { key: string; label: string; warn: boolean; hint: string | null; rows: Claim[] };
type T = (k: string, v?: Record<string, string>) => string;
function subGruposDeSupplementing(t: T, rows: Claim[]): SubGrupo[] {
  const por = new Map<number, Claim[]>();
  for (const c of rows) {
    const n = Math.max(0, c.supplement_attempt?.attempt ?? 0);
    (por.get(n) ?? por.set(n, []).get(n)!).push(c);
  }
  return [...por.keys()].sort((a, b) => a - b).map((n) => ({ key: `attempt:${n}`, label: rotuloDeIntento(t, n), warn: n <= 0, hint: n <= 0 ? t('claims.supNotSubmittedHint') : null, rows: por.get(n)! }));
}
/** "Claim in" en dos (dueño 2026-10-13: "LOR submitted, LOR not submitted"): lo que falta mandar va primero. */
function subGruposDeClaimIn(t: T, rows: Claim[]): SubGrupo[] {
  const sin = rows.filter((c) => !c.lor_sent_at), con = rows.filter((c) => Boolean(c.lor_sent_at));
  const out: SubGrupo[] = [];
  if (sin.length) out.push({ key: 'lor:no', label: t('claims.lorNotSubmitted'), warn: true, hint: t('claims.lorNotSubmittedHint'), rows: sin });
  if (con.length) out.push({ key: 'lor:yes', label: t('claims.lorSubmitted'), warn: false, hint: null, rows: con });
  return out;
}
/** Mitigation (dueño 2026-10-13): por estimar · hecho sin mandar · estimado enviado · attempt N = el N.º estimado enviado (los follow-ups viven dentro del intento). */
function rotuloDeMitigacion(t: T, m: NonNullable<Claim['mitigation']>): string {
  if (m.kind === 'to_estimate') return t('claims.mitToEstimate');
  if (m.attempt <= 0) return t('claims.mitNotSent');
  if (m.attempt === 1) return t('claims.mitEstimateSent');
  return t('claims.mitAttempt', { n: String(m.attempt) });
}
function subGruposDeMitigacion(t: T, rows: Claim[]): SubGrupo[] {
  const orden = (m: NonNullable<Claim['mitigation']>) => (m.kind === 'to_estimate' ? -1 : m.attempt);
  const por = new Map<number, Claim[]>();
  for (const c of rows) { const k = orden(c.mitigation!); (por.get(k) ?? por.set(k, []).get(k)!).push(c); }
  return [...por.keys()].sort((a, b) => a - b).map((k) => {
    const m = por.get(k)![0]!.mitigation!;
    return { key: `mit:${k}`, label: rotuloDeMitigacion(t, m), warn: k <= 0, hint: k === -1 ? t('claims.mitToEstimateHint') : k === 0 ? t('claims.mitNotSentHint') : null, rows: por.get(k)! };
  });
}
/** Qué etapas se parten en sub-grupos y cómo. Las demás, una sola lista. */
function subGruposDe(t: T, stage: string, rows: Claim[]): SubGrupo[] | null {
  if (stage === 'Supplementing') return subGruposDeSupplementing(t, rows);
  if (stage === 'Claim in') return subGruposDeClaimIn(t, rows);
  if (stage === GRUPO_MITIGACION) return subGruposDeMitigacion(t, rows);
  return null;
}
/** Chip del estado de mitigación en la columna Status (solo en las filas del grupo Mitigation). */
function MitigationChip({ c }: { c: Claim }) {
  const { t } = useI18n();
  const m = c.mitigation;
  if (!m) return null;
  const tono = m.kind === 'to_estimate' || m.attempt <= 0 ? 'orange' : m.attempt === 1 ? 'blue' : 'purple';
  const titulo = [m.last_sent_at ? t('claims.supLastSent', { d: new Date(m.last_sent_at).toLocaleDateString(localeUI()) }) : null, m.follow_ups > 0 ? t('claims.mitFollowUps', { n: String(m.follow_ups) }) : null].filter(Boolean).join(' · ');
  return (
    <>
      <span className={`badge ${tono}`} title={titulo || undefined}>{rotuloDeMitigacion(t, m)}</span>
      {m.follow_ups > 0 && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('claims.mitFollowUps', { n: String(m.follow_ups) })}</span>}
    </>
  );
}
/** Chip de la LOR en la columna Status (solo en Claim in). */
function LorChip({ c }: { c: Claim }) {
  const { t } = useI18n();
  if (c.lifecycle_status !== 'Claim in') return null;
  return c.lor_sent_at
    ? <span className="badge green" title={t('claims.lorSentOn', { d: new Date(c.lor_sent_at).toLocaleDateString(localeUI()) })}>{t('claims.lorSubmitted')}</span>
    : <span className="badge orange">{t('claims.lorNotSubmitted')}</span>;
}
/** El rótulo del sub-grupo / chip por número de intento. */
function rotuloDeIntento(t: T, n: number): string {
  if (n <= 0) return t('claims.supNotSubmitted');
  if (n === 1) return t('claims.supSubmitted');
  if (n === 2) return t('claims.supSecond');
  if (n === 3) return t('claims.supThird');
  return t('claims.supNth', { n: String(n) });
}
/** Chip del sub-status en la columna Status (solo en Supplementing) + lo que falta: el seguro contestó / counter listo sin mandar. */
function SupplementAttemptChips({ c }: { c: Claim }) {
  const { t } = useI18n();
  const a = c.supplement_attempt;
  if (!a) return null;
  const n = Math.max(0, a.attempt);
  const tono = n <= 0 ? 'orange' : n === 1 ? 'blue' : n === 2 ? 'purple' : 'green';
  return (
    <>
      <span className={`badge ${tono}`} title={a.last_sent_at ? t('claims.supLastSent', { d: new Date(a.last_sent_at).toLocaleDateString(localeUI()) }) : undefined}>{rotuloDeIntento(t, n)}</span>
      {a.carrier_replied && <RChip variant="warn" icon={Timer}>{t('claims.supCarrierReplied')}</RChip>}
      {!a.carrier_replied && a.counter_ready && <RChip variant="no" icon={FileCheck}>{t('claims.supCounterReady')}</RChip>}
    </>
  );
}

const daysSince = (iso: string | null | undefined): number | null =>
  iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null;

/** Tinta por estado del follow-up (tokens → dark correcto). El semáforo es el mismo de la app:
 *  ok=verde · vence hoy=naranja · vencido=rojo · 2d+=morado (manager alert). */
/** Tira de chips de readiness del claim (fotos / intake / welcome / contract / policy / días) —
 *  igual que Claim Review. Rediseño Tech Premium: RChip (variantes con tokens, dark incluido). */
function ReadinessChips({ c, photosOnly }: { c: Claim; photosOnly?: boolean }) {
  const { t } = useI18n();
  const chips: React.ReactNode[] = [];
  // Fotos: ya NO va como chip — vive en el circulito verde/rojo junto al nombre (pedido del
  // dueño: el chip de abajo hacía la fila más alta y el hover marcaba una franja gris rara).
  // Después de Claim Review no queda ningún chip: la fila es de UNA sola línea.
  if (photosOnly) return null;
  // Intake questions.
  if (c.intake_status) {
    const map = { complete: ['ok', t('iq.qComplete')], incomplete: ['warn', t('iq.qIncomplete')], missing: ['no', t('iq.qMissing')] } as const;
    const [variant, label] = map[c.intake_status];
    chips.push(<RChip key="iq" variant={variant} icon={FilePenLine}>{label}</RChip>);
  }
  // Welcome call.
  chips.push(<RChip key="wc" variant={c.welcome_call_done ? 'ok' : 'warn'} icon={Phone}>{c.welcome_call_done ? t('iq.wcDone') : t('iq.wcPending')}</RChip>);
  // Contract + Policy (checks verde/rojo).
  const chk = (ok: boolean, label: string, key: string) => <RChip key={key} variant={ok ? 'ok' : 'no'} icon={ok ? Check : X}>{label}</RChip>;
  chips.push(chk(!!c.contract_signed, t('iq.contract'), 'ct'), chk(!!c.has_policy, t('iq.policy'), 'pl'));
  // Días en la etapa (7+ = rojo, como siempre).
  const d = daysSince(c.status_since);
  if (d != null) chips.push(<RChip key="d" variant={d >= 7 ? 'no' : 'neutral'} icon={Timer}>{d}d</RChip>);
  // Follow-up del DEPARTAMENTO activo: último update en su canal + próximo follow-up (rojo si venció).
  const fuShort = (iso: string) => new Date(iso).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' });
  if (c.last_follow_up_at) chips.push(<RChip key="lfu" icon={CalendarDays} title={t('fu.lastFu')}>{fuShort(c.last_follow_up_at)}</RChip>);
  return <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap', alignItems: 'center' }}>{chips}</span>;
}

/**
 * Chip "metido en Welcome Call" — SIEMPRE visible (a diferencia de ReadinessChips, que se
 * esconde después de Claim Review). Feedback 2026-07-29: "los que están en welcome call no me
 * lo muestra en la vista del card principal" — el claim sigue en Claim Accepted (post Claim
 * Review) mientras espera en la cola, así que sin esto era invisible en la vista principal. La
 * × hace bypass directo, sin abrir el claim.
 */
function WelcomeCallStuckChip({ c, onChanged }: { c: Claim; onChanged: () => void }) {
  const { t } = useI18n();
  const [busy, setBusy] = useState(false);
  if (!c.in_welcome_call) return null;
  async function bypass(e: React.MouseEvent) {
    e.stopPropagation();
    if (!window.confirm(t('claims.wcBypassConfirm', { name: c.homeowner_name || c.claim_number || `#${c.id}` }))) return;
    setBusy(true);
    try { await api.post(`/api/claims/${c.id}/welcome-call/bypass`, {}); onChanged(); }
    finally { setBusy(false); }
  }
  return (
    <span className="rchip warn" title={t('claims.wcStuckHint')} style={{ padding: '2.5px 4px 2.5px 9px', gap: 3 }}>
      <Phone size={11} strokeWidth={2} /> {t('claims.inWelcomeCall')}
      <button type="button" disabled={busy} onClick={bypass} title={t('wc.bypassHint')}
        style={{ border: 0, background: 'transparent', color: 'inherit', cursor: 'pointer', fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)', lineHeight: 1, padding: '0 2px' }}>
        ×
      </button>
    </span>
  );
}

/** CLAIM status (etapa del ciclo de vida). Separado del status de Payments. */
const NO_STAGE = SIN_ETAPA; // clave compartida con el Finder (lib/lifecycle)
const stageOf = (c: { lifecycle_status: string | null }) => c.lifecycle_status || NO_STAGE;
/** Estados TEMPRANOS: el claim aún no tiene RCV/pagos/suplementos → fila simplificada.
 *  Lead = sin nada; los demás muestran solo "Claims team:". */
/** Chips de readiness completos SOLO hasta Claim Review; después → solo el chip de fotos. */
const REVIEW_IDX = LIFECYCLE.indexOf('Claim Review');
const showAllChips = (stage: string) => { const i = LIFECYCLE.indexOf(stage); return i !== -1 && i <= REVIEW_IDX; };
/** Fecha+hora corta para las columnas de update. */
/** Etapas "cerradas" del claim (para ocultarlas con el toggle). */
const CLOSED_STAGES = new Set(['Finalized', 'Project Completed', 'Claim Completed', 'Archived']);

/** Tarjeta de claim con foto de la casa. La URL de la foto es determinística (proxy por claim),
 *  así que NO se hace un fetch por tarjeta (antes: N+1 de property-intel). La imagen se carga
 *  lazy (solo cuando entra al viewport) para no bajar 2000 fotos de una. */
/**
 * LOS TRES DEPARTAMENTOS DENTRO DE LAS COLUMNAS LAST/NEXT UPDATE — un renglón por depto.
 *
 * Boceto de Manuel (2026-08-20): "lo veo muy desorganizado, lo prefiero así". Antes hubo un
 * chip único con el follow-up más urgente de cualquier depto (un número sin dueño: "26d late")
 * y después una tira horizontal suelta debajo del nombre, que no se leía contra los
 * encabezados. Acá cada renglón vive DENTRO de su columna —la fecha bajo LAST UPDATE, los días
 * bajo NEXT UPDATE— y las dos celdas usan el mismo alto de línea para que Claims, Suppl. y
 * Payments queden a la misma altura y se lean como una tabla.
 *
 * El grado (°) marca el follow-up CALCULADO por la cadencia del depto: no hay fecha agendada.
 * Donde el claim no está presente va una raya, no un número inventado.
 */
const DEPTOS_FU = ['claims', 'supplements', 'payments'] as const;
const LINEA_FU: React.CSSProperties = { height: 16, display: 'flex', alignItems: 'center', gap: 6 };

function TresDeptos({ c, campo }: { c: Claim; campo: 'last' | 'next' }) {
  const { t } = useI18n();
  const porDepto = c.follow_ups_by_dept;
  if (!porDepto || Object.keys(porDepto).length === 0) return <span className="muted">—</span>;
  const corto = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString(localeUI(), { month: 'short', day: 'numeric' });
  return (
    <div style={{ display: 'grid', gap: 2 }}>
      {DEPTOS_FU.map((k) => {
        const v = porDepto[k];
        if (campo === 'last') {
          return (
            <div key={k} style={LINEA_FU}>
              <span className="muted" style={{ fontSize: 'var(--fs-2xs)', fontWeight: 700, minWidth: 56, textTransform: 'uppercase', letterSpacing: 0.3 }}>{t(`fu3.${k}`)}</span>
              {/* NUNCA ES "NUNCA", NO UNA FECHA. La columna caía a la fecha de entrada al status
                  y mostraba el día en que se creó la fila del departamento como si alguien
                  hubiera escrito (2.484 filas en prod). Si el depto ni siquiera está trabajando
                  el claim (sin etapa), no hay nada que decir: guion. */}
              <span style={{ fontFamily: 'var(--font-mono)', color: v?.last ? 'var(--ink)' : 'var(--muted)' }}>
                {v?.last ? corto(v.last) : v && !v.idle ? <i style={{ fontStyle: 'normal', opacity: 0.7 }}>{t('fu3.never')}</i> : '—'}
              </span>
            </div>
          );
        }
        /* EL TEXTO Y EL COLOR, DE LA MISMA FUENTE (Manuel 2026-08-20: "usa los mismos colores,
           lo veo on time de diferentes colores, y lo late siempre rojo").
           El texto salía de `days_late` y el color de followUpState(next) — dos cálculos
           distintos sobre husos distintos (la BD compara con CURRENT_DATE, el navegador con la
           hora local), así que podían contradecirse y aparecía "on time" pintado de rojo.
           Ahora los dos salen de `days_late`: vencido = ROJO siempre, vence hoy = ámbar, y con
           días por delante = verde. Además va la FECHA junto a los días, como se pidió. */
        const dl = v?.days_late ?? null;
        const color = dl == null ? 'var(--muted)' : dl > 0 ? 'var(--red)' : dl === 0 ? 'var(--orange, #b45309)' : 'var(--green)';
        return (
          <div key={k} style={LINEA_FU}
            title={v?.next ? `${t('fu.nextFu')}: ${corto(v.next)}${v.scheduled ? '' : ` · ${t('fu3.calc')}`}` : v?.idle ? t('fu3.idle') : t('fu3.none')}>
            {v?.next && dl != null ? (
              <>
                <span style={{ fontFamily: 'var(--font-mono)', color: 'var(--ink)' }}>{corto(v.next)}</span>
                <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color }}>
                  {dl > 0 ? t('fu3.late', { d: String(dl) }) : dl === 0 ? t('fu3.today') : t('fu3.in', { d: String(-dl) })}
                  {!v.scheduled ? <span style={{ opacity: 0.45 }}>°</span> : ''}
                </span>
              </>
            ) : <span className="muted">—</span>}
          </div>
        );
      })}
    </div>
  );
}

function ClaimCard({ c }: { c: Claim }) {
  const { t } = useI18n();
  const img = c.has_photo ? `/api/claims/${c.id}/streetview` : null;
  return (
    <Link
      to={`/claims/${c.id}`}
      style={{ display: 'block', textDecoration: 'none', color: 'inherit', border: '1px solid var(--line)', borderRadius: 14, overflow: 'hidden', background: 'var(--card)' }}
    >
      <div style={{ height: 150, background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
        {img
          ? <img src={img} loading="lazy" alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <span className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{t('claims.noPhoto')}</span>}
      </div>
      <div style={{ padding: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
          {/* Claim # en Geist Mono (regla del rediseño: IDs y montos en mono). */}
          <strong style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-sm)', letterSpacing: '-0.02em' }}>{c.claim_number || `#${c.id}`}</strong>
          <span className={`badge ${statusColor(stageOf(c))}`}>{stageOf(c)}</span>
        </div>
        <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {c.homeowner_name || '—'}
          <ClaimTypeChip claimId={c.id} claim={c} size="sm" />
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>{c.insurance_company || '—'}</div>
        <div style={{ marginTop: 6, fontWeight: 'var(--fw-semibold)', fontFamily: 'var(--font-mono)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{formatUsd(c.rcv_amount_cents)}</div>
      </div>
    </Link>
  );
}

/** Modal para cambiar el CLAIM STATUS (etapa). Al elegir, el backend fija el lifecycle_status y
 *  RUTEA el claim al depto/cola de esa etapa (ej. "Claim Review" → cola Claim Review de Intake). */
function StatusPickerModal({ claim, onClose, onSaved }: { claim: Claim; onClose: () => void; onSaved: () => void }) {
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const current = claim.lifecycle_status || '';
  async function pick(status: string) {
    if (status === current) { onClose(); return; }
    setBusy(status); setErr('');
    try { await api.post(`/api/claims/${claim.id}/lifecycle-status`, { status }); onSaved(); onClose(); }
    catch (e) { setErr(e instanceof ApiError ? e.message : 'Error'); setBusy(''); }
  }
  const overlay: React.CSSProperties = { position: 'fixed', inset: 0, zIndex: 4000, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 };
  const modal: React.CSSProperties = { background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 14, width: 420, maxWidth: '100%', maxHeight: '85vh', overflowY: 'auto', padding: 18 };
  return (
    <div style={overlay} onClick={onClose}>
      <div style={modal} onClick={(e) => e.stopPropagation()}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <b style={{ flex: 1 }}>Cambiar claim status</b>
          <button className="btn ghost sm" onClick={onClose}><X size={14} strokeWidth={1.75} /></button>
        </div>
        <div className="muted" style={{ fontSize: 'var(--fs-xs)', marginBottom: 10 }}>
          {claim.homeowner_name || claim.claim_number} · al elegir, el claim se mueve a la cola de ese depto (ej. Claim Review).
        </div>
        {err && <div style={{ color: 'var(--red)', fontSize: 'var(--fs-xs)', marginBottom: 8 }}>{err}</div>}
        <div style={{ display: 'grid', gap: 4 }}>
          {LIFECYCLE.map((s) => (
            <button key={s} className={`btn sm ${s === current ? '' : 'ghost'}`} style={{ justifyContent: 'flex-start' }}
              disabled={!!busy} onClick={() => pick(s)}>
              {busy === s ? '…' : s}{s === current ? '  (actual)' : ''}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/** ¿Estamos en móvil (≤900px)? Hook simple con matchMedia + listener, para que el board
 *  cambie de tabla a cards al vuelo si se rota el teléfono o se achica la ventana. */
function useIsMobile(): boolean {
  const [mobile, setMobile] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 900px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 900px)');
    const onChange = (e: MediaQueryListEvent) => setMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return mobile;
}

/** Card de claim de la vista MÓVIL (mockup B-mobile, phone "1 · Claims"): dot del color de la
 *  etapa + nombre en bold + chip del claim# en mono + chevron; debajo la dirección en muted con
 *  ellipsis; abajo el badge de la etapa + dots mini de readiness + los días en la etapa a la
 *  derecha (verde fresco / ámbar / rojo viejo, mismo umbral de 7d que ReadinessChips).
 *  Click = exactamente lo mismo que la fila de desktop (abre el drawer del claim). */
function MobileClaimCard({ c, color, onOpen }: { c: Claim; color: string; onOpen: () => void }) {
  const { t } = useI18n();
  const stage = stageOf(c);
  const d = daysSince(c.status_since);
  const dCls = d == null ? 'ok' : d >= 7 ? 'bad' : d >= 4 ? 'warn' : 'ok';
  // Dots mini (18px, solo ícono): los MISMOS estados que muestran ReadinessChips y la tabla —
  // fotos (circulito verde/rojo de la fila), contrato firmado y póliza.
  const dots: Array<{ key: string; ok: boolean; Icon: typeof Camera; title: string }> = [
    { key: 'ph', ok: !!c.has_photos, Icon: Camera, title: c.has_photos ? t('iq.photosUp') : t('iq.photosWait') },
    { key: 'ct', ok: !!c.contract_signed, Icon: FileCheck, title: t('iq.contract') },
    { key: 'pl', ok: !!c.has_policy, Icon: Shield, title: t('iq.policy') },
  ];
  return (
    <button type="button" className="mclaim" onClick={onOpen}>
      <span className="mclaim-top">
        <span className="mclaim-dot" style={{ background: color }} />
        <span className="mclaim-nm">{c.homeowner_name || '—'}</span>
        {c.claim_number && <span className="mclaim-ref">{c.claim_number}</span>}
        {/* Acceso rápido a las FOTOS desde la fila, sin desplegar (pedido del dueño). Span y no
            <a>: la card entera ya es un <button> y un link anidado es HTML inválido en iOS. */}
        {c.photos_url && (
          <span
            className="mclaim-photos"
            role="link"
            title={t('iq.photosUp')}
            onClick={(e) => { e.stopPropagation(); window.open(c.photos_url!, '_blank', 'noopener'); }}
          ><Camera size={15} strokeWidth={1.9} /></span>
        )}
        <span className="mclaim-chev"><ChevronRight size={15} strokeWidth={2} /></span>
      </span>
      {c.address && <span className="mclaim-addr">{c.address}</span>}
      <span className="mclaim-bot">
        <span className={`badge ${statusColor(stage)}`}>{stage}</span>
        <span className="mclaim-rdots">
          {dots.map(({ key, ok, Icon, title }) => (
            <span key={key} className={`mclaim-rdot ${ok ? 'ok' : 'no'}`} title={title}><Icon size={10} strokeWidth={2} /></span>
          ))}
        </span>
        {d != null && <span className={`mclaim-days ${dCls}`}><span className="mclaim-dd" />{d}d</span>}
      </span>
    </button>
  );
}

/** Modal centrado para elegir UNA opción, con buscador — mismo patrón del MemberSelect de
 *  src/lib/scope.tsx (ya convertido a modal), copiado acá porque aquel componente es privado
 *  y scope.tsx no se toca. Backdrop y Esc cierran. */
function MPickerModal({ placeholder, options, loading, onSelect, onClose }: {
  placeholder: string;
  options: Array<{ id: string; label: string; on?: boolean }>;
  loading?: boolean;
  onSelect: (id: string) => void;
  onClose: () => void;
}) {
  const [q, setQ] = useState('');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const ql = q.trim().toLowerCase();
  const filtered = ql ? options.filter((o) => o.label.toLowerCase().includes(ql)) : options;
  return (
    <div className="mclaim-modal-backdrop" onClick={onClose}>
      <div className="mclaim-modal" onClick={(e) => e.stopPropagation()}>
        <input className="input" autoFocus placeholder={placeholder} value={q} onChange={(e) => setQ(e.target.value)} style={{ width: '100%', marginBottom: 6 }} />
        <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
          {loading ? (
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 6 }}>…</div>
          ) : filtered.length === 0 ? (
            <div className="muted" style={{ fontSize: 'var(--fs-2xs)', padding: 6 }}>—</div>
          ) : (
            filtered.map((o) => (
              <button key={o.id} className={`btn ${o.on ? '' : 'ghost '}sm`}
                style={{ display: 'block', width: '100%', textAlign: 'left', minHeight: 40 }}
                onClick={() => onSelect(o.id)}>
                {o.label}
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

/** Modal "Persona" del móvil: gente + EQUIPOS — la misma carga que hace el MemberSelect de
 *  ScopeTabs (oculto en móvil); al elegir se disparan los mismos setMember/setScope. */
function MPersonModal({ onSelect, onClose }: { onSelect: (assignee: string) => void; onClose: () => void }) {
  const { t } = useI18n();
  const [opts, setOpts] = useState<Array<{ id: string; name: string; assignee: string }> | null>(null);
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get<{ members: Array<{ id: string; name: string; assignee: string }> }>('/api/team/members')
        .then((r) => r.members).catch(() => [] as Array<{ id: string; name: string; assignee: string }>),
      api.get<{ teams: Array<{ id: string; name: string; is_individual: boolean }> }>('/api/teams/options')
        .then((r) => r.teams.filter((tm) => !tm.is_individual).map((tm) => ({ id: `team-${tm.id}`, name: tm.name, assignee: tm.name })))
        .catch(() => [] as Array<{ id: string; name: string; assignee: string }>),
    ]).then(([people, teams]) => { if (alive) setOpts([...teams, ...people]); });
    return () => { alive = false; };
  }, []);
  return (
    <MPickerModal
      placeholder={t('mclaims.pickPerson')}
      loading={opts === null}
      options={(opts ?? []).map((o) => ({ id: o.id, label: o.name }))}
      onSelect={(id) => { const m = opts?.find((x) => x.id === id); if (m) onSelect(m.assignee); }}
      onClose={onClose}
    />
  );
}

export function Claims() {
  const { t } = useI18n();
  const { me } = useAuth();
  const [claims, setClaims] = useState<Claim[]>([]);
  const [loadErr, setLoadErr] = useState('');
  const [statusEdit, setStatusEdit] = useState<Claim | null>(null);
  const canEditStatus = hasRole(me, 'manager');
  // Vista MÓVIL (≤900px): cards del mockup en vez de la tabla. Mismos datos y filtros; el
  // buscador de arriba y los pickers de etapa/persona son estado LOCAL de esta pantalla.
  const isMobile = useIsMobile();
  const [mQuery, setMQuery] = useState('');
  const [mStagePick, setMStagePick] = useState(false);
  const [mPersonPick, setMPersonPick] = useState(false);
  const [mCoPick, setMCoPick] = useState(false); // chip Contractor del mockup (móvil)
  // Agrupar por status (como Monday). Grupos COLAPSADOS por defecto. Persistido (sessionStorage) →
  // al volver del claim completo (atrás) se restaura qué grupos estaban desplegados. null = aún sin
  // decidir (primera visita) → el effect los colapsa todos.
  const [collapsedArr, setCollapsedArr] = usePersistentState<string[] | null>('claims:collapsed', null);
  const collapsed = useMemo(() => new Set(collapsedArr ?? []), [collapsedArr]);
  const toggleGroup = (s: string) => setCollapsedArr((prev) => { const n = new Set(prev ?? []); if (n.has(s)) n.delete(s); else n.add(s); return [...n]; });
  // Filtros (estilo Monday) + orden por columna.
  const [showFilter, setShowFilter] = useState(false);
  // Búsqueda/filtros/fila expandida PERSISTEN (sessionStorage) → al volver del claim completo
  // con "atrás", la vista queda EXACTAMENTE como estaba (queja masiva del equipo).
  const [fContractor, setFContractor] = usePersistentState<string>('claims:fContractor', '');
  const [fInsurance, setFInsurance] = usePersistentState<string>('claims:fInsurance', '');
  // Fila DESPLEGADA inline: muestra la vista del depto dueño del status (como el menú izquierdo).
  const [expandedId, setExpandedId] = usePersistentState<string | null>('claims:expandedRow', null);
  const [sortKey, setSortKey] = useState<'claim_number' | 'homeowner_name' | 'contractor_name' | 'insurance_company' | ''>('');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [view, setView] = usePersistentState<'list' | 'card'>('claims:view', 'list');
  const [showForm, setShowForm] = useState(false);
  const [num, setNum] = useState('');
  const [name, setName] = useState('');
  const [rcv, setRcv] = useState('');
  const [address, setAddress] = useState('');
  const [busy, setBusy] = useState(false);
  const [hideClosed, setHideClosed] = usePersistentState<boolean>('claims:hideClosed', false);
  const [sel, setSel] = usePersistentState<string | null>('claims:sel', null);
  const [statusFilter, setStatusFilter] = usePersistentState<string | null>('claims:statusFilter', null);
  // Deep-link de filtro por etapa: /claims?status=Waiting%20Documents (los KPIs del Intake
  // Dashboard llegan así). SOLO al montar: fija el statusFilter y expande los grupos para que
  // la lista filtrada se vea de una (con filtro solo queda un grupo, así que expandir todo es
  // exactamente ese). Después manda el UI local — limpiar el filtro no toca la URL.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const s = searchParams.get('status');
    if (s) { setStatusFilter(s); setCollapsedArr([]); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Selección múltiple para acciones en bulk (pedido 2026-08-03: "un checkmark para seleccionar
  // múltiples claims y asignar algún equipo en bulk"). NO se persiste a propósito: volver mañana
  // y encontrarse 40 claims tildados de ayer es la forma más fácil de reasignar algo sin querer.
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [bulkTeam, setBulkTeam] = useState('');
  const [bulkBusy, setBulkBusy] = useState(false);
  const [teamOpts, setTeamOpts] = useState<Array<{ id: string; name: string; is_individual?: boolean }>>([]);
  const togglePick = (id: string) => setPicked((prev) => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  // Scope de EQUIPO: un claim "es" de su account manager Y de todos los miembros de los equipos
  // asignados (Rose ve los claims del equipo de Darwin, no solo los que tiene ella directo).
  // Haystack del filtro por persona: el asignado + los MIEMBROS de los teams + los NOMBRES de los
  // teams — así el selector puede filtrar tanto por una persona como por un equipo completo.
  const sc = useScope(claims, (c) => [
    c.assigned_person,
    c.team_member_names,
    ...(c.assigned_teams ?? []).map((t) => t.team),
  ].filter(Boolean).join(', '));

  // Depto activo (el mismo del switcher del sidebar): cada depto ve SUS fechas de follow-up.
  const activeDept = (typeof localStorage !== 'undefined' && localStorage.getItem('activeDept')) || 'claims';
  async function load() {
    const qs = new URLSearchParams();
    qs.set('department', activeDept);
    const r = await api.get<{ claims: Claim[] }>(`/api/claims?${qs.toString()}`);
    setClaims(r.claims);
    setLoadErr('');
  }
  /** Carga inicial de la lista: si falla, se DICE. Antes era una promesa sin atrapar (el
   *  `setTimeout(load)` de abajo) y la pantalla se quedaba con la lista vacía, sin explicación
   *  — que es justo lo que se ve cuando el backend está reiniciándose por un deploy. */
  const loadOrErr = () => { void load().catch((e) => setLoadErr(e instanceof ApiError ? e.message : 'Error')); };

  // EL BULK ASIGNA EL CLAIMS TEAM (dueño 2026-10-07: "cuando selecciono todo quiero asignarlo a los
  // claims team, no a supplement department"): el equipo dueño del claim —el del departamento Claims—,
  // no el equipo de trabajo del depto que quedó activo en el navegador (`activeDept` seguía en
  // Supplements por haber navegado ese board, y la barra asignaba y listaba equipos de Supplements).
  useEffect(() => {
    api.get<{ teams: Array<{ id: string; name: string; is_individual?: boolean }> }>('/api/teams/options?department=claims')
      .then((r) => setTeamOpts(r.teams)).catch(() => setTeamOpts([]));
  }, []);

  /** Asigna el equipo elegido a TODOS los seleccionados. */
  async function assignBulk() {
    const ids = [...picked];
    if (!ids.length || !bulkTeam) return;
    const team = teamOpts.find((x) => x.id === bulkTeam);
    // Confirmación con el número y el nombre adelante: reasignar 40 claims sin querer se
    // deshace de a uno, y nadie se acuerda de cuáles eran.
    if (!window.confirm(t('claims.bulkConfirm', { n: String(ids.length), team: team?.name ?? '' }))) return;
    setBulkBusy(true);
    try {
      const r = await api.post<{ updated: number; skipped: number }>('/api/claims/bulk/assigned-team', {
        claimIds: ids, department: 'claims', teamId: bulkTeam,
      });
      setPicked(new Set()); setBulkTeam('');
      await load();
      alert(t('claims.bulkDone', { n: String(r.updated), same: String(r.skipped) }));
    } catch (e) { alert(e instanceof ApiError ? e.message : 'Error'); }
    finally { setBulkBusy(false); }
  }

  useEffect(() => {
    const t = setTimeout(loadOrErr, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDept]);

  async function create() {
    setBusy(true);
    try {
      await api.post('/api/claims', {
        claimNumber: num || undefined,
        homeownerName: name || undefined,
        address: address || undefined,
        rcvAmountCents: rcv ? Math.round(parseFloat(rcv) * 100) : undefined,
      });
      setNum('');
      setName('');
      setRcv('');
      setAddress('');
      setShowForm(false);
      await load();
    } finally {
      setBusy(false);
    }
  }

  // Opciones de los filtros (valores distintos presentes en el scope actual).
  const contractorOpts = [...new Set(sc.shown.map((c) => c.contractor_name).filter(Boolean) as string[])].sort();
  const insuranceOpts = [...new Set(sc.shown.map((c) => c.insurance_company).filter(Boolean) as string[])].sort();
  // Base: scope de persona + (opcional) ocultar cerrados + filtros de contractor/insurance.
  let base = hideClosed ? sc.shown.filter((c) => !CLOSED_STAGES.has(stageOf(c))) : sc.shown;
  if (fContractor) base = base.filter((c) => (c.contractor_name || '') === fContractor);
  if (fInsurance) base = base.filter((c) => (c.insurance_company || '') === fInsurance);
  // Conteos por CLAIM status.
  const statusCounts: Record<string, number> = {};
  for (const c of base) {
    const s = stageOf(c);
    statusCounts[s] = (statusCounts[s] || 0) + 1;
  }
  const known = new Set(LIFECYCLE);
  const extraStatuses = Object.keys(statusCounts).filter((s) => s !== NO_STAGE && !known.has(s)).sort();
  const visible = statusFilter ? base.filter((c) => stageOf(c) === statusFilter) : base;
  // Agrupa los visibles por status en el ORDEN del ciclo de vida (como los grupos de Monday).
  // Las etapas no listadas y "Sin etapa" van al final.
  const cmp = (a: Claim, b: Claim): number => {
    if (!sortKey) return 0;
    const d = String(a[sortKey] ?? '').localeCompare(String(b[sortKey] ?? ''), undefined, { numeric: true });
    return sortDir === 'asc' ? d : -d;
  };
  const groups = conGruposVirtuales(agruparPorEtapa(visible, stageOf, sortKey ? cmp : undefined), visible);

  // Primera visita (sin estado guardado): arranca con TODOS los grupos colapsados.
  useEffect(() => {
    if (collapsedArr === null && groups.length) setCollapsedArr(groups.map((g) => g.stage));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collapsedArr, groups.length]);

  // Color estable por grupo de status (el mismo que usa el Finder — lib/lifecycle).
  const groupColor = colorDeGrupo;
  // Opciones del filtro por status (todas las etapas presentes + Sin etapa + desconocidas).
  const statusFilterOpts = [...LIFECYCLE.filter((s) => statusCounts[s]), ...(statusCounts[NO_STAGE] ? [NO_STAGE] : []), ...extraStatuses];
  const anyFilter = !!(fContractor || fInsurance || statusFilter);

  // MÓVIL: lista PLANA con los mismos grupos (orden del ciclo de vida) aplanados, más el
  // buscador local de arriba (nombre / dirección / claim#) sobre la lista ya cargada.
  const mq = mQuery.trim().toLowerCase();
  const mRows = groups.flatMap((g) => g.rows);
  const mVisible = mq
    ? mRows.filter((c) =>
        (c.homeowner_name || '').toLowerCase().includes(mq) ||
        (c.address || '').toLowerCase().includes(mq) ||
        (c.claim_number || '').toLowerCase().includes(mq))
    : mRows;

  return (
    <div className="grid" style={{ gap: 14 }}>
      {/* Barra estilo Monday: New client | Person | Filter | Sort | Hide | Group by.
          La BÚSQUEDA por texto se movió al buscador GLOBAL de la barra superior (Finder) — así
          es una sola caja que funciona desde cualquier página (pedido de Manuel). */}
      {/* En móvil (≤900px) la toolbar de desktop NO se muestra: la reemplazan el buscador y los
          chips del mockup B-mobile (abajo), que disparan los mismos filtros/scope. */}
      {!isMobile && <div className="claims-toolbar" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <button className="btn" onClick={() => setShowForm((s) => !s)}>{showForm ? t('common.cancel') : <><Plus size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('claims.newClient')}</>}</button>
        <span style={{ borderLeft: '1px solid var(--line)', height: 20 }} />
        {/* Person */}
        <ScopeTabs {...sc} memberOptions={undefined} />
        <span style={{ borderLeft: '1px solid var(--line)', height: 20 }} />
        {/* Filter */}
        <button className={`btn ghost sm${anyFilter ? ' active' : ''}`} onClick={() => setShowFilter((v) => !v)}>▽ {t('claims.filter')}{anyFilter ? ' •' : ''}</button>
        {/* Sort */}
        <select className="input sm" style={{ maxWidth: 130 }} value={sortKey} onChange={(e) => setSortKey(e.target.value as typeof sortKey)} title={t('claims.sort')}>
          <option value="">↕ {t('claims.sort')}</option>
          <option value="homeowner_name">{t('claims.homeowner')}</option>
          <option value="contractor_name">{t('cdw.contractor')}</option>
          <option value="insurance_company">{t('q.insurance')}</option>
          <option value="claim_number">{t('claims.num')}</option>
        </select>
        {sortKey && <button className="btn ghost sm" onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))} title={t('claims.sortDir')}>{sortDir === 'asc' ? '▲' : '▼'}</button>}
        {/* Hide */}
        <button className={`btn ghost sm${hideClosed ? ' active' : ''}`} onClick={() => setHideClosed((a) => !a)}><EyeOff size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {hideClosed ? t('claims.showClosed') : t('claims.hideClosed')}</button>
        {/* Group by (fijo: Status) */}
        <span className="btn ghost sm" style={{ cursor: 'default', opacity: 0.85 }}>▦ {t('claims.groupStatus')}</span>
        <span style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }}>
          <button className={`btn ghost sm${view === 'list' ? ' active' : ''}`} onClick={() => setView('list')}>{t('claims.viewList')}</button>
          <button className={`btn ghost sm${view === 'card' ? ' active' : ''}`} onClick={() => setView('card')}>{t('claims.viewCard')}</button>
        </div>
      </div>}

      {/* Filtros (toggle desde "Filter") */}
      {!isMobile && showFilter && view === 'list' && (
        <div className="card" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: 10 }}>
          <select className="input sm" style={{ maxWidth: 220 }} value={fContractor} onChange={(e) => setFContractor(e.target.value)}>
            <option value="">{t('claims.allContractors')}</option>
            {contractorOpts.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select className="input sm" style={{ maxWidth: 220 }} value={fInsurance} onChange={(e) => setFInsurance(e.target.value)}>
            <option value="">{t('claims.allInsurance')}</option>
            {insuranceOpts.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select className="input sm" style={{ maxWidth: 240 }} value={statusFilter ?? ''} onChange={(e) => setStatusFilter(e.target.value || null)}>
            <option value="">{t('claims.allStatus')}</option>
            {statusFilterOpts.map((s) => <option key={s} value={s}>{s} ({statusCounts[s] || 0})</option>)}
          </select>
          <button className="btn ghost sm" onClick={() => setCollapsedArr(groups.map((g) => g.stage))}>▸ {t('claims.collapseAll')}</button>
          <button className="btn ghost sm" onClick={() => setCollapsedArr([])}>▾ {t('claims.expandAll')}</button>
          {anyFilter && <button className="btn ghost sm" onClick={() => { setFContractor(''); setFInsurance(''); setStatusFilter(null); }}><X size={14} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('common.clear')}</button>}
        </div>
      )}

      {showForm && (
        <div className="card">
          <h2 className="card-title">{t('claims.createTitle')}</h2>
          <div className="grid cols-3">
            <Field label={t('claims.num')} value={num} onChange={setNum} required={false} />
            <Field label={t('claims.homeowner')} value={name} onChange={setName} required={false} />
            <Field label={t('claims.rcv')} value={rcv} onChange={setRcv} required={false} />
          </div>
          <AddressAutocomplete label={t('set.address')} value={address} onChange={(v) => setAddress(v)} />
          <button className="btn" disabled={busy} onClick={create}>{busy ? '…' : t('common.create')}</button>
        </div>
      )}

      {loadErr && (
        <div style={{ color: 'var(--red)', fontSize: 'var(--fs-sm)', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span>{loadErr}</span>
          <button className="btn ghost sm" onClick={loadOrErr}>{t('common.retry')}</button>
        </div>
      )}

      {!isMobile && hasRole(me, 'super-manager') && <ClaimsBoardSync onImported={load} />}

      {!isMobile && (
        <div className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>
          <b style={{ fontFamily: 'var(--font-mono)' }}>{visible.length}</b> {t('claims.claimsWord')}{anyFilter ? ` (${t('claims.ofTotal', { n: sc.shown.length })})` : ''} · {t('claims.groupedByStatus')}
          {/* QUÉ está recortando la lista, A LA VISTA (Manuel 2026-08-20: "por qué me salen 6
              clientes solamente en claims? le doy a All y no me los muestra"). El chip "All"
              es el scope de PERSONA; el filtro de contratista/aseguradora es otro sistema y
              vivía escondido dentro del panel ▽ Filter, así que la lista se veía recortada sin
              decir por qué. Cada filtro activo se muestra acá con su ✕ para quitarlo de una. */}
          {(fContractor || fInsurance || statusFilter) && (
            <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap', marginLeft: 8, verticalAlign: 'middle' }}>
              {fContractor && (
                <button className="chipx active" onClick={() => setFContractor('')} title={t('common.clear')}>
                  {t('cdw.contractor')}: {fContractor} <X size={12} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} />
                </button>
              )}
              {fInsurance && (
                <button className="chipx active" onClick={() => setFInsurance('')} title={t('common.clear')}>
                  {t('q.insurance')}: {fInsurance} <X size={12} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} />
                </button>
              )}
              {statusFilter && (
                <button className="chipx active" onClick={() => setStatusFilter(null)} title={t('common.clear')}>
                  {statusFilter} <X size={12} strokeWidth={2} style={{ verticalAlign: 'text-bottom' }} />
                </button>
              )}
            </span>
          )}
        </div>
      )}

      {/* Lista + tarjeta lateral en fila: el drawer queda a la DERECHA (sticky), no abajo. */}
      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
      {isMobile ? (
        /* ===== Vista MÓVIL (mockup B-mobile, phone "1 · Claims"): buscador + chips + cards ===== */
        <div className="mclaim-wrap">
          {/* Buscador grande redondeado: filtra por nombre/dirección/claim# sobre la lista ya cargada. */}
          <label className="mclaim-search">
            <Search size={14} strokeWidth={2} />
            <input value={mQuery} onChange={(e) => setMQuery(e.target.value)} placeholder={t('mclaims.search')} />
          </label>
          {/* Chips scrolleables: Etapa (modal) · Mis claims (scope 'mine') · Persona (MemberSelect).
              La envoltura .xscroll pinta el degradado del borde derecho: sin él el último chip
              se leía cortado en vez de continuado. */}
          <div className="xscroll on-bg">
          <div className="mclaim-chips">
            <button type="button" className={`mclaim-fchip${statusFilter ? ' on' : ''}`} onClick={() => setMStagePick(true)}>
              <ListFilter size={12} strokeWidth={2} />
              {statusFilter ? `${t('mclaims.stage')}: ${statusFilter}` : t('mclaims.stageAll')} ▾
            </button>
            <button type="button" className={`mclaim-fchip${sc.scope === 'mine' ? ' on' : ''}`}
              onClick={() => sc.setScope(sc.scope === 'mine' ? 'all' : 'mine')}>
              <Star size={12} strokeWidth={2} /> {t('mclaims.mine')}
            </button>
            {sc.scope === 'member' && sc.member ? (
              <button type="button" className="mclaim-fchip on" onClick={() => { sc.setMember(''); sc.setScope('mine'); }}>
                <Users size={12} strokeWidth={2} /> {sc.member} <X size={11} strokeWidth={2} />
              </button>
            ) : (
              <button type="button" className="mclaim-fchip" onClick={() => setMPersonPick(true)}>
                <Users size={12} strokeWidth={2} /> {t('mclaims.person')}
              </button>
            )}
            {/* Chip Contractor del mockup: mismo filtro fContractor del panel de desktop. */}
            {fContractor ? (
              <button type="button" className="mclaim-fchip on" onClick={() => setFContractor('')}>
                <HardHat size={12} strokeWidth={2} /> {fContractor} <X size={11} strokeWidth={2} />
              </button>
            ) : (
              <button type="button" className="mclaim-fchip" onClick={() => setMCoPick(true)}>
                <HardHat size={12} strokeWidth={2} /> {t('mclaims.contractor')}
              </button>
            )}
          </div>
          </div>
          {mVisible.length === 0 ? (
            <div className="card muted">{t('claims.empty')}</div>
          ) : (
            <div className="mclaim-list">
              {mVisible.map((c) => (
                <MobileClaimCard key={c.id} c={c} color={groupColor(stageOf(c))} onOpen={() => setSel(c.id)} />
              ))}
            </div>
          )}
          {/* Picker de contratista: mismo fContractor del panel de filtros de desktop. */}
          {mCoPick && (
            <MPickerModal
              placeholder={t('mclaims.pickContractor')}
              options={[
                { id: '', label: t('mclaims.allContractors'), on: !fContractor },
                ...contractorOpts.map((x) => ({ id: x, label: x, on: fContractor === x })),
              ]}
              onSelect={(id) => { setFContractor(id); setMCoPick(false); }}
              onClose={() => setMCoPick(false)}
            />
          )}
          {/* Picker de etapa: UNA etapa (las de los grupos actuales) o Todas — mismo statusFilter. */}
          {mStagePick && (
            <MPickerModal
              placeholder={t('mclaims.pickStage')}
              options={[
                { id: '', label: t('mclaims.allStages'), on: !statusFilter },
                ...statusFilterOpts.map((s) => ({ id: s, label: `${s} (${statusCounts[s] || 0})`, on: statusFilter === s })),
              ]}
              onSelect={(id) => { setStatusFilter(id || null); setMStagePick(false); }}
              onClose={() => setMStagePick(false)}
            />
          )}
          {/* Picker de persona: dispara los mismos setMember/setScope que el MemberSelect de desktop. */}
          {mPersonPick && (
            <MPersonModal
              onSelect={(assignee) => { sc.setMember(assignee); sc.setScope('member'); setMPersonPick(false); }}
              onClose={() => setMPersonPick(false)}
            />
          )}
        </div>
      ) : visible.length === 0 ? (
        <div className="card muted">{t('claims.empty')}</div>
      ) : view === 'card' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 14 }}>
          {visible.map((c) => <ClaimCard key={c.id} c={c} />)}
        </div>
      ) : (
        <div>
          {groups.map((g, gi) => {
            const gc = groupColor(g.stage);
            const isCol = collapsed.has(g.stage);
            return (
            <section key={g.stage} style={{ marginTop: gi > 0 ? 14 : 0, background: 'var(--card)', border: '1px solid var(--line)', borderRadius: 'var(--radius)', boxShadow: 'var(--shadow)', overflow: 'hidden' }}>
              {/* Header de grupo (mockup B): caret + punto del color de la etapa + nombre + conteo. */}
              <button onClick={() => toggleGroup(g.stage)}
                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', cursor: 'pointer', background: 'transparent', border: 0, borderBottom: isCol ? 0 : '1px solid var(--line)', padding: '12px 16px', font: 'inherit', textAlign: 'left', fontSize: 'var(--fs-sm)', fontWeight: 'var(--fw-bold)', color: 'var(--ink)' }}>
                <ChevronDown size={15} strokeWidth={2} style={{ color: 'var(--muted)', flexShrink: 0, transition: 'transform 0.18s ease', transform: isCol ? 'rotate(-90deg)' : 'none' }} />
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: gc, flexShrink: 0 }} />
                {g.stage}
                <span style={{ color: 'var(--muted)', fontWeight: 'var(--fw-semibold)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-xs)' }}>— {g.rows.length}</span>
              </button>

              {/* Filas limpias con hairlines (sin reglas verticales ni barra lateral: el color vive en el punto del header). */}
              {!isCol && (
                <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--fs-sm)' }}>
                      <thead>
                        <tr style={{ textAlign: 'left', color: 'var(--muted)' }}>
                          {/* Tildar el encabezado marca TODO EL GRUPO, no toda la tabla: los grupos
                              son el status, y "todos los Waiting Documents" es lo que uno quiere
                              repartir. Un "seleccionar todo" global de 1379 claims sería un accidente
                              esperando pasar. */}
                          <th style={{ padding: '10px 6px 10px 12px', borderBottom: '1px solid var(--line)', width: 30 }}>
                            {(() => {
                              const ids = g.rows.map((x) => x.id);
                              const all = ids.length > 0 && ids.every((id) => picked.has(id));
                              const some = !all && ids.some((id) => picked.has(id));
                              return (
                                <input type="checkbox" checked={all}
                                  ref={(el) => { if (el) el.indeterminate = some; }}
                                  title={t('claims.selectGroup')}
                                  onChange={() => setPicked((prev) => {
                                    const n = new Set(prev);
                                    if (all) ids.forEach((id) => n.delete(id)); else ids.forEach((id) => n.add(id));
                                    return n;
                                  })}
                                  style={{ cursor: 'pointer' }} />
                              );
                            })()}
                          </th>
                          {[t('claims.homeowner'), t('cdw.contractor'), t('q.insurance'), t('cdw.lastUpdate'), t('cdw.nextUpdate'), t('claims.colStatus')].map((h) => (
                            <th key={h} style={{ padding: '10px 12px', fontWeight: 'var(--fw-medium)', fontSize: 'var(--fs-2xs)', textTransform: 'uppercase', letterSpacing: 0.3, borderBottom: '1px solid var(--line)' }}>{h}</th>
                          ))}
                          <th style={{ borderBottom: '1px solid var(--line)' }} />
                        </tr>
                      </thead>
                      <tbody>
                        {/* SUB-GRUPOS (dueño 2026-10-13): Supplementing por intento, Claim in por LOR; las demás etapas, una sola lista. */}
                        {(subGruposDe(t, g.stage, g.rows) ?? [{ key: '', label: '', warn: false, hint: null, rows: g.rows }]).map((sg) => {
                          const sgKey = `${g.stage}::${sg.key}`;
                          const sgCol = Boolean(sg.key) && collapsed.has(sgKey);
                          return (
                          <Fragment key={sgKey || 'all'}>
                            {sg.key && (
                              <tr>
                                <td colSpan={8} style={{ padding: 0, borderTop: '1px solid var(--line)' }}>
                                  <button type="button" onClick={() => toggleGroup(sgKey)}
                                    style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', cursor: 'pointer', background: 'var(--surface-2)', border: 0, padding: '8px 16px 8px 28px', fontSize: 'var(--fs-xs)', fontWeight: 'var(--fw-semibold)', color: 'var(--ink)', textAlign: 'left' }}>
                                    <ChevronDown size={13} strokeWidth={2} style={{ color: 'var(--muted)', flexShrink: 0, transition: 'transform 0.18s ease', transform: sgCol ? 'rotate(-90deg)' : 'none' }} />
                                    <span style={{ width: 7, height: 7, borderRadius: '50%', background: sg.warn ? 'var(--orange)' : groupColor(g.stage), flexShrink: 0 }} />
                                    {sg.label}
                                    <span style={{ color: 'var(--muted)', fontWeight: 'var(--fw-semibold)', fontFamily: 'var(--font-mono)', fontSize: 'var(--fs-2xs)' }}>— {sg.rows.length}</span>
                                    {sg.hint && <span className="muted" style={{ fontWeight: 'var(--fw-regular)', fontSize: 'var(--fs-2xs)' }}>· {sg.hint}</span>}
                                  </button>
                                </td>
                              </tr>
                            )}
                            {!sgCol && sg.rows.map((c, ri) => {
                          const stage = stageOf(c);
                          return (
                          <Fragment key={`${g.stage}:${c.id}`}>
                            <tr className="jf-row" onClick={() => setSel(c.id)}
                              style={{ cursor: 'pointer', background: picked.has(c.id) ? 'var(--accent-soft)' : sel === c.id ? 'var(--accent-soft)' : undefined, borderTop: ri > 0 ? '1px solid var(--line)' : 'none' }}>
                              {/* stopPropagation: tildar NO tiene que abrir el claim. */}
                              <td style={{ padding: '8px 6px 8px 12px' }} onClick={(e) => e.stopPropagation()}>
                                <input type="checkbox" checked={picked.has(c.id)} onChange={() => togglePick(c.id)} style={{ cursor: 'pointer' }} />
                              </td>
                              {/* Homeowner + dirección debajo. Circulito de FOTOS (verde=hay,
                                  clic abre; rojo=faltan) ANTES del caret — reemplaza al chip de
                                  abajo, que estiraba la fila. Caret = despliega la vista del depto. */}
                              <td style={{ padding: '8px 12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  {c.has_photos && c.photos_url ? (
                                    <a href={c.photos_url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                                      className="ph-dot ph-dot-ok" title={t('iq.photosUp')}><Camera size={12} strokeWidth={1.75} /></a>
                                  ) : (
                                    <span className="ph-dot ph-dot-no" title={t('iq.photosWait')}><Camera size={12} strokeWidth={1.75} /></span>
                                  )}
                                  <button onClick={(e) => { e.stopPropagation(); setExpandedId(expandedId === c.id ? null : c.id); }}
                                    style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, color: 'var(--muted)', fontSize: 'var(--fs-2xs)' }}
                                    title={t('claims.expandRow')}>{expandedId === c.id ? '▾' : '▸'}</button>
                                  <div style={{ fontWeight: 'var(--fw-bold)', color: 'var(--ink)' }}>{c.homeowner_name || '—'}</div>
                                  {/* Qué tipo de claim es, al lado del nombre. Nunca en blanco. */}
                                  <ClaimTypeChip claimId={c.id} claim={c} size="sm" onSaved={load} />
                                  {/* Burbuja de UPDATES (como Monday): azul con número si tiene, gris si no.
                                      Clic = abre la tarjeta del claim (el chat está ahí). */}
                                  <span className={`upd-bubble${(c.updates_count ?? 0) > 0 ? ' upd-bubble-on' : ''}`}
                                    title={t('claims.updatesBubble', { n: String(c.updates_count ?? 0) })}>
                                    <MessageSquare size={13} strokeWidth={1.75} />{(c.updates_count ?? 0) > 0 && <span className="upd-bubble-n">{c.updates_count! > 99 ? '99+' : c.updates_count}</span>}
                                  </span>
                                </div>
                                {/* El claim number en la fila (dueño 2026-10-20: "colócame el claim number para que no
                                    se tenga que abrir"). Solo el del seguro; sin él no se inventa nada. */}
                                {(c.claim_number || c.address) && (
                                  <div className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 1, marginLeft: 40 }}>
                                    {c.claim_number && <span className="mono" style={{ fontWeight: 'var(--fw-bold)', color: 'var(--ink)' }}>{c.claim_number}</span>}
                                    {c.claim_number && c.address && ' · '}
                                    {c.address}
                                  </div>
                                )}
                              </td>
                              {/* Contractor (sin icono) */}
                              <td style={{ padding: '10px 12px', fontSize: 'var(--fs-xs)' }}>{c.contractor_name || <span className="muted">—</span>}</td>
                              {/* Insurance (sin icono) */}
                              <td style={{ padding: '10px 12px', fontSize: 'var(--fs-xs)' }}>{c.insurance_company || <span className="muted">—</span>}</td>
                              {/* LAST / NEXT UPDATE: una fila por departamento (ver TresDeptos). */}
                              <td style={{ padding: '8px 12px', fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
                                <TresDeptos c={c} campo="last" />
                              </td>
                              <td style={{ padding: '8px 12px', fontSize: 'var(--fs-2xs)', whiteSpace: 'nowrap' }}>
                                <TresDeptos c={c} campo="next" />
                              </td>
                              {/* Claim status (editable) */}
                              <td style={{ padding: '10px 12px' }} onClick={(e) => e.stopPropagation()}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                  {canEditStatus ? (
                                    <button type="button" className={`badge ${statusColor(stage)}`}
                                      style={{ cursor: 'pointer', border: '1px solid var(--line)', display: 'inline-flex', alignItems: 'center', gap: 4 }}
                                      title="Cambiar claim status" onClick={() => setStatusEdit(c)}>
                                      {stage} <span style={{ opacity: 0.6, fontSize: 'var(--fs-2xs)' }}>▾</span>
                                    </button>
                                  ) : <span className={`badge ${statusColor(stage)}`}>{stage}</span>}
                                  {g.stage === 'Supplementing' && <SupplementAttemptChips c={c} />}
                                  {g.stage === GRUPO_MITIGACION && <MitigationChip c={c} />}
                                  <LorChip c={c} />
                                  <WelcomeCallStuckChip c={c} onChanged={load} />
                                </div>
                              </td>
                              {/* → abre la tarjeta lateral */}
                              <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                                <span style={{ color: 'var(--muted)', display: 'inline-flex' }}><ArrowRight size={16} strokeWidth={1.75} /></span>
                              </td>
                            </tr>
                            {/* Chips ABAJO solo en etapas tempranas (intake/welcome/contract…).
                                Después de Claim Review la fila es de UNA línea — más clientes a
                                la vista, y el hover ya no marca una franja intermedia. */}
                            {showAllChips(stage) && (
                              <tr className="jf-row jf-row-chips" onClick={() => setSel(c.id)} style={{ cursor: 'pointer', background: sel === c.id ? 'var(--accent-soft)' : undefined }}>
                                <td colSpan={8} style={{ padding: '0 12px 9px' }}>
                                  <ReadinessChips c={c} photosOnly={false} />
                                </td>
                              </tr>
                            )}
                            {/* Desplegado inline: la MISMA vista del depto dueño del status (como el menú izquierdo). */}
                            {expandedId === c.id && (
                              <tr>
                                <td colSpan={8} style={{ padding: '4px 12px 16px', background: 'var(--surface-2)' }}>
                                  <ClaimDepartmentTab claimId={c.id} deptKey={c.owner_dept || 'claims'} />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                          );
                        })}
                          </Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                </div>
              )}
            </section>
            );
          })}
        </div>
      )}
        </div>
        {/* En móvil el drawer abre desde la card aunque la vista persistida fuera 'card'. */}
        {sel && (isMobile || view === 'list') && <ClaimDrawer claimId={sel} onClose={() => setSel(null)} showChat />}
      </div>
      {statusEdit && <StatusPickerModal claim={statusEdit} onClose={() => setStatusEdit(null)} onSaved={load} />}

      {/* Barra de acciones en bulk. Aparece SOLO con algo tildado y va fija abajo: con 1379 claims
          agrupados, una barra arriba se pierde de vista apenas se scrollea al grupo que interesa. */}
      {picked.size > 0 && (
        <div style={{
          position: 'fixed', left: 0, right: 0, bottom: 0, zIndex: 300,
          background: 'var(--card)', borderTop: '2px solid var(--accent)', boxShadow: '0 -6px 20px rgba(0,0,0,0.14)',
          padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
        }}>
          <span style={{ fontWeight: 'var(--fw-bold)', fontSize: 'var(--fs-sm)' }}>{t('claims.nSelected', { n: String(picked.size) })}</span>
          <button className="btn ghost sm" onClick={() => setPicked(new Set())}>{t('claims.clearSel')}</button>
          <span style={{ flex: 1 }} />
          <label className="muted" style={{ fontSize: 'var(--fs-xs)' }}>{t('claims.assignTeamTo')}:</label>
          <select className="input sm" style={{ maxWidth: 240 }} value={bulkTeam} onChange={(e) => setBulkTeam(e.target.value)}>
            <option value="">{t('claims.pickTeam')}…</option>
            {teamOpts.map((tm) => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
          </select>
          <button className="btn sm" disabled={bulkBusy || !bulkTeam} onClick={assignBulk}
            style={{ background: 'var(--accent)', color: '#fff', borderColor: 'transparent' }}>
            {bulkBusy ? t('common.loading') : t('claims.assignBulk')}
          </button>
          {/* Que se vea QUÉ equipo es: el claims team (dueño del claim), no el equipo de trabajo de un depto. */}
          <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('claims.bulkClaimTeam')}</span>
        </div>
      )}
    </div>
  );
}
